import type { ReactNode } from "react";
import { Button } from "./Button";
import { cx } from "../utils/cx";

export type OperationStatusKind =
  | "idle"
  | "running"
  | "success"
  | "error"
  | "cancelled";

export interface OperationStatusProps {
  status: OperationStatusKind;
  title: string;
  description?: string;
  progress?: number;
  actions?: ReactNode;
  onCancel?: () => void;
  cancelLabel?: string;
  className?: string;
}

export function OperationStatus({
  status,
  title,
  description,
  progress,
  actions,
  onCancel,
  cancelLabel = "Cancel",
  className,
}: OperationStatusProps) {
  const normalizedProgress =
    progress === undefined
      ? undefined
      : Math.max(0, Math.min(1, progress));
  return (
    <section
      role={status === "error" ? "alert" : "status"}
      aria-live="polite"
      aria-busy={status === "running"}
      className={cx(
        "ui-operation-status",
        `ui-operation-status--${status}`,
        className,
      )}
    >
      <span className="ui-operation-status__indicator" aria-hidden="true" />
      <div className="ui-operation-status__copy">
        <strong>{title}</strong>
        {description ? <p>{description}</p> : null}
        {normalizedProgress !== undefined ? (
          <progress
            aria-label={title}
            max={1}
            value={normalizedProgress}
          />
        ) : null}
      </div>
      {status === "running" && onCancel ? (
        <Button size="small" variant="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
      ) : actions ? (
        <div className="ui-operation-status__actions">{actions}</div>
      ) : null}
    </section>
  );
}
